import Api from '~/store/idonia-api/api'
import useLanguageTree from '~/commons/language'
import app, { resetSessionInactivity } from '~/main'
import FileDownloadUpdated from './notifications/FileDownloadUpdated.js'
import IdoniaNotifications from '~/store/idonia-api/notifications/notifications/_all.js'
import isEqual from 'lodash/isEqual'

// Warning
const payloadMinLength = 2 // MINIMUM LENGTH  OF AN AGGREGATED PAYLOAD -> {notification_name: '', pending_aggregate: true}

const {t} = useLanguageTree()
const parseNotification = function (payload, id, read_at) {
  if (Object.keys(payload).length <= payloadMinLength) {
    return {
      notification_name: payload.notification_name,
      ipn_id: id || payload.ipn_id,
      payload: payload,
      emptyPayload: true
    }
  }
  let name = payload.notification_name
  if (payload.pending_aggregate !== undefined) {
    switch (name) {
      case 'file.shared.new':
      case 'file.shared.deleted':
        name = 'file.aggregate'
        break
    }
  }
  const computed = IdoniaNotifications[name] || {}
  const namespaced = ['notification'].concat(payload.notification_name.split('.'))

  const notification = {
    notification_name: payload.notification_name,
    is_volatile: !!payload.is_volatile,
    payload: {...payload},
    ipn_id: id || payload.ipn_id,
    read_at: read_at || payload.read_at
  }
  if (!notification.is_volatile) {
    const favicon = document.getElementById('favicon-custom')
    let iconUrl = '/static/images/ipn/default.png'
    if (favicon) {
      iconUrl = 'white'
    }
    notification.title = computed.title ? computed.title(payload) : t(namespaced.concat(['title']).join('.'), payload)
    notification.body = computed.body ? computed.body(payload) : t(namespaced.concat(['body']).join('.'), payload)
    if (favicon) {
      notification.avatar = computed.icon ? computed.icon(payload) : favicon.href
    }
    notification.icon = computed.icon ? computed.icon(payload) : iconUrl

    if (typeof computed.click === 'function') {
      notification.click = computed.click
    }
  }

  if (typeof computed.background === 'function') {
    notification.background = computed.background
  }
  delete notification.payload['is_volatile']
  delete notification.payload['ipn_id']
  delete notification.payload['read_at']
  return notification
}

export default {
  namespaced: true,
  state: {
    notifications: [],
    pendingNotifications: [],
    count: 10,
    page: 0,
    newNotifications: 0,
    enabledDesktop: window.Notification && Notification.permission === 'granted',
    wsUrl: '',
    wsID: '',
    socket: null,
    guestSockets: {},
    lastNotification: {}
  },
  getters: {
    newNotifications: state => {
      return state.newNotifications
    },
    enabledDesktop: state => {
      return window.Notification && Notification.permission === 'granted'
    },
    allNotifications: state => {
      return state.notifications
    }
  },
  mutations: {
    addNotification (state, notification) {
      state.lastNotification = notification
      state.notifications.unshift(notification)
    },
    addNewNotification (state) {
      state.newNotifications++
    },
    addPage (state) {
      state.page++
    },
    clearPage (state) {
      state.page = 0
    },
    clearNewNotifications (state) {
      state.newNotifications = 0
    },
    addNotifications (state, notification) {
      state.notifications.push(notification)
    },
    insertNotifications (state, notifications) {
      if (notifications.length === 0) return
      notifications.sort((a, b) => {
        const pA = a.position
        const pB = b.position
        if (pA < pB) return -1
        if (pA > pB) return 1
        return 0
      }).forEach((n) => {
        state.notifications.splice(n.position, 0, n.notification)
      })
    },
    deleteNotifications (state, indexes = []) {
      if (indexes.length === 0) return
      indexes.sort().reverse().forEach((index) => {
        state.notifications.splice(index, 1)
      })
    },
    clearNotifications (state, notifications) {
      state.pendingNotifications = state.notifications.filter(n => !!n.payload.pending_aggregate || n.payload.is_tmp)
      state.notifications = []
    },
    clearPendingNotifications (state) {
      state.pendingNotifications = []
    },
    setNotifications (state, notifications) {
      state.notifications = notifications
    },
    enabledDesktop (state, enabled) {
      state.enabledDesktop = enabled
    },
    setWebsocket (state, {sha, socket}) {
      if (sha) {
        state.guestSockets = {
          ...state.guestSockets,
          [sha]: {socket}
        }
      } else {
        state.socket = socket
      }
    },
    setWebsocketID (state, {sha, wsID}) {
      if (sha) {
        state.guestSockets[sha]['wsID'] = wsID
        state.guestSockets = {
          ...state.guestSockets
        }
      } else {
        Api.config.wsID = wsID
        state.wsID = wsID
      }
    },
    clearWebsocket (state) {
      if (state.socket !== null) {
        state.socket.onclose = () => {}
        state.socket.close()
      }
      state.socket = null
    }
  },
  actions: {
    updateNotRead ({state, commit}) {
      let params = {
        count: state.count,
        page: 1,
        'sort[]': '-created_at'
      }

      return Api.getV2('notifications', params)
        .then(response => {
          commit('clearNewNotifications')
          ;(response.body.results || []).forEach(ipn => {
            if (!ipn.read_at) {
              commit('addNewNotification')
            }
          })
          return response
        })
        .catch(err => { console.log(JSON.stringify(err)) })
    },
    enableDesktop ({commit}) {
      commit('enabledDesktop', true)
    },
    addNotification ({dispatch, commit, state}, {notification, containsDuplicateInfo = false}) {
      commit('addNotification', notification)
      let alreadyNotified = false
      if (containsDuplicateInfo) {
        alreadyNotified = state.notifications.find(n => n.ipn_id === notification.ipn_id)
      }
      if (!containsDuplicateInfo || !alreadyNotified) {
        commit('addNewNotification')
        if (state.enabledDesktop) {
          dispatch('desktopNotify', notification)
        }
      }
    },
    desktopNotify (state, notification) {
      /* eslint-disable no-new */
      new Notification(notification.ipn_id, {
        title: notification.title,
        message: notification.body,
        iconUrl: window.location.origin + '/' + notification.icon
      })
    },
    clearPage ({commit}) {
      commit('clearPage')
    },
    clearNewNotifications ({commit}) {
      commit('clearNewNotifications')
    },
    clearNotifications ({commit}) {
      commit('clearNotifications')
    },
    async addOrAggregate ({state, commit, dispatch}, {notifications = [], isNew = false}) {
      if (!Array.isArray(notifications)) {
        notifications = [notifications]
      }
      if (notifications.length === 0) {
        return
      }
      const IDs = new Set()
      const pendingIDs = new Set()
      const toAggregate = {}
      state.notifications.forEach(function (notification) {
        if (notification.ipn_id) IDs.add(notification.ipn_id)
      })
      state.pendingNotifications.forEach(function (notification) {
        if (notification.ipn_id) pendingIDs.add(notification.ipn_id)
      })
      const toDelete = new Set()
      notifications.forEach(function (notification) {
        if (IDs.has(notification.ipn_id) || pendingIDs.has(notification.ipn_id)) {
          const alreadySet = ([...state.notifications, ...state.pendingNotifications]).find((n) => n.ipn_id === notification.ipn_id)
          const areEqual = isEqual(alreadySet.payload, notification.payload)
          if (!!alreadySet && !areEqual) {
            toDelete.add(notification.ipn_id)
            if (!toAggregate[notification.ipn_id]) {
              toAggregate[notification.ipn_id] = {
                ipn_id: notification.ipn_id,
                notification_name: notification.notification_name,
                payloads: [notification.payload, alreadySet.payload]}
            } else {
              toAggregate[notification.ipn_id].payloads.push(notification.payload)
            }
          } else if (areEqual && pendingIDs.has(notification.ipn_id)) {
            commit('addNotifications', notification)
          }
        } else {
          if (isNew) dispatch('addNotification', {notification})
          else if (!notification.emptyPayload) {
            commit('addNotifications', notification)
          }
        }
      })
      const previousPositions = dispatch('deleteNotificationByIDs', toDelete)
      const toInsert = []

      for (const ipnID in toAggregate) {
        const aggregated = await dispatch('aggregateNotifications', toAggregate[ipnID])
        const reparsedNotification = parseNotification(aggregated)
        if (isNew) dispatch('addNotification', {notification: reparsedNotification, containsDuplicateInfo: true})
        else toInsert.push({ notification: reparsedNotification, position: previousPositions[ipnID] })
      }
      commit('insertNotifications', toInsert)
    },
    getNotifications ({state, commit, dispatch}) {
      commit('addPage')
      let params = {
        count: state.count,
        page: state.page,
        'sort[]': '-created_at'
      }

      return Api.getV2('notifications', params)
        .then(response => {
          const notifications = []

          commit('clearNewNotifications')
          ;(response.body.results || []).forEach(ipn => {
            if (!ipn.read_at) {
              commit('addNewNotification')
            }
            ipn.payload = ipn.payload || {}
            ipn.payload['notification_name'] = ipn.payload['notification_name'] || ipn.notification_name
            const payload = parseNotification(ipn.payload, ipn.ipn_id, ipn.read_at)
            if (payload) {
              notifications.push(payload)
            }
          })
          dispatch('addOrAggregate', { notifications }).then(() => {
            commit('clearPendingNotifications')
          })
          return response
        })
        .catch(err => { console.log(JSON.stringify(err)) })
    },
    setNotificationRead ({dispatch}, ipn_id) {
      return Api.putV2('notifications/' + ipn_id)
        .then(() => dispatch('getNotifications'))
    },
    askPermission ({commit}) {
      return new Promise((resolve, reject) => {
        if (window.Notification) {
          if (Notification.permission !== 'granted') {
            Notification.requestPermission(function (status) {
              if (status === 'granted') {
                commit('enabledDesktop', true)
                resolve()
              } else {
                commit('enabledDesktop', false)
                reject(new Error('Declined'))
              }
            })
          } else {
            commit('enabledDesktop', true)
            reject(new Error('Already granted'))
          }
        } else {
          commit('enabledDesktop', false)
          reject(new Error('No window.notification'))
        }
      })
    },
    setWebsocket (context, config) {
      const {commit, dispatch} = context
      const {sha, socket} = config

      if (socket) {
        if (!socket.hasListenersOn) {
          socket.on('connect', () => {
            console.info('[websocket ' + socket.id + '] connected')
            commit('setWebsocketID', {sha, wsID: socket.id})
          })
          socket.on('reconnect_attempt', () => {
            console.info('[websocket ' + socket.id + '] attempting reconnection')
          })
          socket.on('message', (type, payload) => {
            console.info('[websocket ' + socket.id + '] message received: ', type, payload)
            if (payload['notification_name'] && payload['notification_name'] === FileDownloadUpdated.name) {
              resetSessionInactivity()
            }
            payload['notification_name'] = payload['notification_name'] || type
            const notification = parseNotification(payload, payload.ipn_id, payload.read_at)
            if (!notification) {
              return
            }
            if (!notification.is_volatile) {
              dispatch('addOrAggregate', { notifications: notification, isNew: true })
            }

            if (typeof notification.background === 'function') {
              notification.background.call(app || this, notification.payload, context, config)
            }
          })
          socket.hasListenersOn = true
        }
        commit('setWebsocket', {sha, socket})
      }
    },
    clearWebsocket ({commit}) {
      Api.sockets = new Map()
      commit('setWebsocketID', {wsID: ''})
      commit('clearWebsocket')
    },
    deleteNotificationByIDs ({state, commit}, ids) {
      const idPositions = {}
      ids.forEach((id) => {
        const index = state.notifications.findIndex((n) => n.ipn_id === id)
        if (index !== -1) {
          idPositions[id] = index
        }
      })
      commit('deleteNotifications', Object.values(idPositions))
      return idPositions
    },
    aggregateNotifications ({state}, ipns = {}) {
      if (!ipns || ipns.payloads.length === 0) {
        return null
      }
      let result = {
        ipn_id: ipns.ipn_id,
        notification_name: ipns.notification_name
      }
      if (ipns.payloads.length === 1) {
        result = {
          ...result,
          ...ipns.payloads[0]
        }
        return result
      }

      switch (result.notification_name) {
        case 'file.shared.new':
        case 'file.shared.deleted':
          return aggregateAddedOrDeletedFile(result, ipns.payloads)
        default:
          throw new Error('Unimplemented aggregation for ' + result.notification_name)
      }
    }
  }
}
function aggregateAddedOrDeletedFile (result = {}, ipns = []) {
  const toMerge = []
  const actors = {}

  for (const ipn of ipns) {
    if (ipn.pending_aggregate !== undefined) {
      toMerge.push(ipn)
      continue
    }
    if (!actors[ipn.actor_name]) {
      actors[ipn.actor_name] = {
        actor_name: ipn.actor_name,
        parents: {}
      }
    }
    const file = {
      file_name: ipn.file_name,
      file_url: ipn.file_url,
      file_id: ipn.file_id
    }
    if (!actors[ipn.actor_name].parents[ipn.parent_name]) {
      actors[ipn.actor_name].parents[ipn.parent_name] = {
        parent_name: ipn.parent_name,
        files: []
      }
    }
    if (!actors[ipn.actor_name].parents[ipn.parent_name].files.find(f => f.file_id === file.file_id)) {
      actors[ipn.actor_name].parents[ipn.parent_name].files.push(file)
    }
  }
  const arrayActors = []
  for (const actor in actors) {
    const parentsArray = []
    for (const parent in actors[actor].parents) {
      parentsArray.push(actors[actor].parents[parent])
    }
    arrayActors.push({
      actor_name: actor,
      parents: parentsArray
    })
  }
  result.pending_aggregate = true
  result.actors = arrayActors

  if (toMerge.length === 0) {
    return result
  }
  return mergeAddedOrDeletedFile(toMerge, result)
}

function mergeAddedOrDeletedFile (toMerge = [], receivedResult = {}) {
  const result = {
    ipn_id: receivedResult.ipn_id,
    notification_name: receivedResult.notification_name,
    pending_aggregate: true
  }
  toMerge.push(receivedResult)
  const actors = {}
  for (const ipn of toMerge) {
    if (ipn.pending_aggregate === false) {
      return ipn
      // CLEAN IDB
    }
    if (!ipn.actors || ipn.actors.length === 0) {
      continue
    }
    for (const actor of ipn.actors) {
      if (!actors[actor.actor_name]) {
        actors[actor.actor_name] = {
          actor_name: actor.actor_name,
          parents: {}
        }
      }
      for (const parent of actor.parents) {
        if (!actors[actor.actor_name].parents[parent.parent_name]) {
          actors[actor.actor_name].parents[parent.parent_name] = {
            parent_name: parent.parent_name,
            files: parent.files
          }
        } else {
          for (const file of parent.files) {
            if (!actors[actor.actor_name].parents[parent.parent_name].files.find(f => f.file_id === file.file_id)) {
              actors[actor.actor_name].parents[parent.parent_name].files.push(file)
            }
          }
        }
      }
    }
  }
  const arrayActors = []
  for (const actor in actors) {
    const parentsArray = []
    for (const parent in actors[actor].parents) {
      parentsArray.push(actors[actor].parents[parent])
    }
    arrayActors.push({
      actor_name: actor,
      parents: parentsArray
    })
  }
  result.actors = arrayActors
  return result
}



// WEBPACK FOOTER //
// ./src/store/idonia-api/notifications/notifications.js